import { useCallback, useState, type SyntheticEvent } from 'react'
import type { ToastType } from '../../contexts/toast-context'
import { fetchWithPolicy } from '../../lib/api/fetchPolicy'
import { bootstrapAdminCsrfToken, resolveCsrfHeaderForMethod } from '../../lib/api/csrfToken'
import { API_BASE } from '../admin/constants'
import {
  createPasskeyAuthenticationResponse,
  isPasskeyAuthenticationSupported,
} from '../admin/profile-manager/passkeyWebauthn'
import { normalizeRedirectTarget, REDIRECT_QUERY_PARAM } from '../admin/redirectTarget'
import type { AdminAppState } from '../useAdminApp'
import { LOGIN_PATH, LOGIN_PATH_REGEX } from '../admin/redirectTarget'
import { getAdminHomePath } from '../admin/adminPath'

type ShowToast = (
  type: ToastType,
  title: string,
  message?: string,
) => void

export const useAdminAppUiActions = ({
  app,
  showToast,
  t,
}: {
  app: AdminAppState
  showToast: ShowToast
  t: (key: string, params?: Record<string, string | number>) => string
}) => {
  const [commandOpenState, setCommandOpenState] = useState(false)
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)
  const loginErrorMessage = "Impossibile effettuare l'accesso. Riprova."

  const commandOpen = app.isAuthenticated && commandOpenState

  const handleCommandOpenChange = useCallback(
    (next: boolean) => {
      if (next && !app.isAuthenticated) return
      setCommandOpenState(next)
    },
    [app.isAuthenticated],
  )

  const handleLogout = useCallback(async () => {
    await app.logout()
    showToast('success', t('auth.logoutSuccess'), t('auth.logoutMessage'))
    setCommandOpenState(false)
  }, [app, showToast, t])

  const confirmLogout = useCallback(() => {
    setLogoutDialogOpen(true)
  }, [])

  const closeLogoutDialog = useCallback(() => {
    setLogoutDialogOpen(false)
  }, [])

  const resolveGatewayMessage = useCallback(
    (payload: Record<string, unknown> | null, fallbackKey: string): string => {
      if (!payload) return t(fallbackKey)
      const nested =
        payload.error && typeof payload.error === 'object'
          ? (payload.error as Record<string, unknown>)
          : null
      const nestedMessage = typeof nested?.message === 'string' ? nested.message.trim() : ''
      if (nestedMessage) return nestedMessage
      const message = typeof payload.message === 'string' ? payload.message.trim() : ''
      return message || t(fallbackKey)
    },
    [t],
  )

  const handleLoginWithToast = useCallback(
    async (event: SyntheticEvent<HTMLFormElement>) => {
      try {
        const result = await app.handleLogin(event)
        if (result && typeof result === 'object' && 'otpRequired' in result && result.otpRequired) {
          return
        }
        showToast('success', t('auth.loginSuccess'), t('auth.loginMessage'))
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Admin login failed', error)
        }
        const errorCode =
          error &&
          typeof error === 'object' &&
          'code' in error &&
          typeof (error as { code?: unknown }).code === 'string'
            ? ((error as { code: string }).code ?? '')
            : ''
        const isAuthRequired =
          errorCode === 'AUTH_REQUIRED' ||
          (error instanceof Error && /\bAUTH_REQUIRED\b/i.test(error.message))
        const isRateLimited =
          errorCode === 'RATE_LIMITED' ||
          (error instanceof Error &&
            /too many requests|troppi tentativi|retry after|riprova tra/i.test(error.message))
        const isOnLoginPage =
          typeof window !== 'undefined' && LOGIN_PATH_REGEX.test(window.location.pathname)
        if (isAuthRequired && !app.isAuthenticated && !isOnLoginPage && typeof window !== 'undefined') {
          window.location.replace(LOGIN_PATH)
        }
        const visibleMessage = isRateLimited && error instanceof Error && error.message.trim()
          ? error.message
          : loginErrorMessage
        showToast('error', t('auth.loginError'), visibleMessage)
      }
    },
    [app, loginErrorMessage, showToast, t],
  )

  const runPasskeyAuthWithToast = useCallback(async () => {
    try {
      const sessionId = app.loginForm.twoFactorSessionId?.trim() ?? ''
      if (!sessionId) {
        throw new Error(t('auth.passkey.error.session'))
      }
      if (!isPasskeyAuthenticationSupported()) {
        throw new Error(t('auth.passkey.error.notSupported'))
      }

      const optionsResponse = await fetchWithPolicy({
        request: `${API_BASE}/auth/admin/passkeys/auth/options`,
        init: {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
        },
      })

      const optionsPayload = (await optionsResponse.json().catch(() => null)) as Record<string, unknown> | null
      if (!optionsResponse.ok) {
        throw new Error(resolveGatewayMessage(optionsPayload, 'auth.passkey.error.start'))
      }

      const options = optionsPayload?.options
      if (!options || typeof options !== 'object') {
        throw new Error(t('auth.passkey.error.start'))
      }

      const authenticationResponse = await createPasskeyAuthenticationResponse(
        options as Record<string, unknown>,
      )

      const verifyResponse = await fetchWithPolicy({
        request: `${API_BASE}/auth/admin/passkeys/auth/verify`,
        init: {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionId,
            response: authenticationResponse,
          }),
        },
      })

      const verifyPayload = (await verifyResponse.json().catch(() => null)) as Record<string, unknown> | null
      if (!verifyResponse.ok) {
        throw new Error(resolveGatewayMessage(verifyPayload, 'auth.passkey.error.verify'))
      }

      await bootstrapAdminCsrfToken(API_BASE)
      const validateResponse = await fetchWithPolicy({
        request: `${API_BASE}/auth/validate`,
        init: {
          method: 'POST',
          credentials: 'include',
          headers: resolveCsrfHeaderForMethod('POST'),
        },
        retries: 0,
      })
      if (!validateResponse.ok) {
        throw new Error(t('auth.error.invalid'))
      }

      if (typeof window !== 'undefined') {
        const redirectRaw = new URLSearchParams(window.location.search).get(REDIRECT_QUERY_PARAM)
        const safeRedirect = normalizeRedirectTarget(redirectRaw, window.location.origin)
        window.location.replace(safeRedirect ?? getAdminHomePath())
        return
      }

      showToast('success', t('auth.loginSuccess'), t('auth.loginMessage'))
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Admin passkey login failed', error)
      }
      const message = error instanceof Error && error.message.trim()
        ? error.message
        : t('auth.passkey.error.generic')
      showToast('error', t('auth.passkey.error.title'), message)
    }
  }, [app.loginForm.twoFactorSessionId, resolveGatewayMessage, showToast, t])

  return {
    commandOpen,
    logoutDialogOpen,
    handleCommandOpenChange,
    handleLogout,
    confirmLogout,
    closeLogoutDialog,
    handleLoginWithToast,
    runPasskeyAuthWithToast,
    setCommandOpenState,
  }
}
